import React from 'react';
import { TrendingUp, Clock, Users, Calendar, MapPin, Zap, Star, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

// Array of hotel images for variety
const HOTEL_IMAGES = [
  'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80', // Hotel exterior
  'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80', // Hotel room
  'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&q=80', // Pool view
  'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=800&q=80', // Luxury hotel
  'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80', // Modern room
  'https://images.unsplash.com/photo-1618773928121-c32242e63f39?w=800&q=80', // Bedroom
  'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800&q=80', // Suite
  'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800&q=80', // Resort
];

// Get a consistent image based on deal id or index
const getImageForDeal = (deal, index = 0) => {
  if (deal.cover_image) return deal.cover_image;
  if (deal.property_details?.photos?.length > 0) return deal.property_details.photos[0];
  // Use hash of id or index to get consistent image
  const hash = deal.id ? deal.id.split('').reduce((a, b) => a + b.charCodeAt(0), 0) : index;
  return HOTEL_IMAGES[hash % HOTEL_IMAGES.length];
};

const DealCardModern = ({ deal, onBook, onCounterOffer, index = 0 }) => {
  const discount = (deal.original_price && deal.discounted_price && deal.original_price > 0)
    ? Math.round((1 - deal.discounted_price / deal.original_price) * 100)
    : (deal.discount_percent || 0);

  const daysUntil = Math.floor(
    (new Date(deal.check_in) - new Date()) / (1000 * 60 * 60 * 24)
  );

  const getUrgencyLevel = () => {
    if (daysUntil <= 3) return { text: 'Last Minute', color: 'bg-red-500', pulse: true };
    if (daysUntil <= 7) return { text: 'Offerta Limitata', color: 'bg-amber-500', pulse: false };
    return { text: 'Offerta Speciale', color: 'bg-emerald-500', pulse: false };
  };

  const urgency = getUrgencyLevel();
  const coverImage = getImageForDeal(deal, index);

  return (
    <Card className="group overflow-hidden bg-white hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 border-0 shadow-lg">
      {/* Image Section */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={coverImage}
          alt={deal.room_type_name || 'Hotel room'}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          onError={(e) => {
            e.target.src = HOTEL_IMAGES[0];
          }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        
        {/* Top Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          <Badge className={`${urgency.color} text-white border-0 font-semibold shadow-lg ${urgency.pulse ? 'animate-pulse' : ''}`}>
            {urgency.text}
          </Badge>
          {deal.available_units <= 2 && (
            <Badge className="bg-white/90 backdrop-blur-sm text-slate-900 border-0 font-medium">
              Solo {deal.available_units} rimaste
            </Badge>
          )}
        </div>

        {/* Discount Badge */}
        {discount > 0 && (
          <div className="absolute top-3 right-3">
            <div className="bg-white rounded-full px-3 py-1.5 shadow-lg flex items-center gap-1">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              <span className="font-bold text-emerald-600">-{discount}%</span>
            </div>
          </div>
        )}

        {/* Bottom Info on Image */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-xl font-bold text-white mb-1 drop-shadow-lg">
            {deal.room_type_name}
          </h3>
          <div className="flex items-center gap-2 text-white/90">
            <MapPin className="w-4 h-4" />
            <span className="text-sm font-medium">{deal.city}</span>
            {deal.property_details?.name && (
              <>
                <span className="text-white/50">•</span>
                <span className="text-sm">{deal.property_details.name}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-5">
        {/* Date and Guests Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Date</p>
              <p className="text-sm font-semibold text-slate-900">
                {deal.check_in?.slice(5)} - {deal.check_out?.slice(5)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Capacità</p>
              <p className="text-sm font-semibold text-slate-900">
                {deal.capacity || deal.available_units} {deal.capacity === 1 ? 'ospite' : 'ospiti'}
              </p>
            </div>
          </div>
        </div>

        {/* Description */}
        {deal.description && (
          <p className="text-sm text-slate-600 mb-4 line-clamp-2">
            {deal.description}
          </p>
        )}

        {/* Timer Alert */}
        {daysUntil <= 7 && (
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-3 mb-4">
            <Clock className="w-5 h-5 text-amber-600" />
            <div className="flex-1">
              <p className="text-xs text-amber-700 font-medium">Disponibile ancora per</p>
              <p className="text-sm font-bold text-amber-900">{daysUntil} giorni</p>
            </div>
          </div>
        )}

        {/* Price and Actions */}
        <div className="flex items-end justify-between pt-4 border-t border-slate-100">
          <div>
            {deal.original_price && deal.original_price !== deal.discounted_price && (
              <p className="text-sm text-slate-400 line-through">€{deal.original_price}</p>
            )}
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-black text-slate-900">€{deal.discounted_price}</span>
              <span className="text-sm text-slate-500">/notte</span>
            </div>
          </div>

          <div className="flex gap-2">
            {onCounterOffer && (
              <Button 
                onClick={onCounterOffer}
                variant="outline"
                size="sm"
                className="border-slate-200 hover:bg-slate-50"
              >
                Proponi
              </Button>
            )}
            <Button 
              onClick={onBook}
              className="bg-slate-900 hover:bg-slate-800 text-white px-6"
            >
              Prenota
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default DealCardModern;
